import { Fragment, useMemo, type ReactNode } from "react";

/**
 * Renders a model answer as the small Markdown subset the chat prompt asks for:
 * paragraphs, "-" bullets one level deep, **bold** and `code`.
 *
 * Built as React nodes rather than HTML, so nothing the model writes can arrive
 * as markup. Anything outside the subset is left as the characters it is: a
 * stray asterisk reads better than a half-supported syntax that swallows it.
 */
export function AnswerText({ text }: { text: string }) {
  const blocks = useMemo(() => parseBlocks(text), [text]);
  return (
    <>
      {blocks.map((b, i) =>
        b.kind === "list" ? (
          <ul key={i} className="answer-list">
            {b.items.map((item, j) => (
              <li key={j} className={item.depth ? "nested" : undefined}>
                <Inline text={item.text} />
              </li>
            ))}
          </ul>
        ) : (
          <p key={i} className="answer-p">
            <Inline text={b.text} />
          </p>
        ),
      )}
    </>
  );
}

type Block = { kind: "para"; text: string } | { kind: "list"; items: Array<{ text: string; depth: number }> };

const BULLET = /^(\s*)[-*]\s+(.*)$/;

function parseBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  let para: string[] = [];
  const flush = () => {
    if (para.length) blocks.push({ kind: "para", text: para.join("\n") });
    para = [];
  };
  for (const line of text.split("\n")) {
    const bullet = BULLET.exec(line);
    if (bullet) {
      flush();
      const item = { text: bullet[2]!.trim(), depth: bullet[1]!.length >= 2 ? 1 : 0 };
      const last = blocks[blocks.length - 1];
      if (last?.kind === "list") last.items.push(item);
      else blocks.push({ kind: "list", items: [item] });
      continue;
    }
    if (!line.trim()) {
      flush();
      continue;
    }
    para.push(line.trim());
  }
  flush();
  return blocks;
}

const INLINE = /(\*\*[^*\n]+\*\*|`[^`\n]+`)/g;

function Inline({ text }: { text: string }) {
  return (
    <>
      {text.split(INLINE).map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) return <strong key={i}>{part.slice(2, -2)}</strong>;
        if (part.startsWith("`") && part.endsWith("`") && part.length > 2) return <code key={i}>{part.slice(1, -1)}</code>;
        return <Fragment key={i}>{part as ReactNode}</Fragment>;
      })}
    </>
  );
}
